import React, { useState, useEffect } from 'react';
import { Ship, Plus, Edit2, Trash2, X, Check, AlertCircle, Calendar } from 'lucide-react';
import apiService from '../services/api';

const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => Math.max(0, prev - decrement));
    }, interval);

    const closeTimer = setTimeout(onClose, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const config = {
    success: { bgColor: 'bg-white border border-green-200 shadow-soft', progressColor: 'bg-green-500', textColor: 'text-green-800' },
    error: { bgColor: 'bg-white border border-red-200 shadow-soft', progressColor: 'bg-red-500', textColor: 'text-red-800' }
  };

  const currentConfig = config[type] || config.success;

  return (
    <div className={`${currentConfig.bgColor} ${currentConfig.textColor} rounded-xl overflow-hidden min-w-[320px] max-w-md animate-slideIn`}>
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-relaxed break-words">{message}</p>
        </div>
        <button onClick={onClose} className="flex-shrink-0 hover:bg-ink-100 rounded-lg p-1.5 transition-all duration-200 ml-2">
          <X size={18} />
        </button>
      </div>
      <div className="h-1 bg-ink-100">
        <div className={`h-full ${currentConfig.progressColor} transition-all duration-100 ease-linear`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

function Navieras() {
  const [navieras, setNavieras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingNaviera, setEditingNaviera] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [nombreNaviera, setNombreNaviera] = useState('');
  const [diasLibres, setDiasLibres] = useState(21);

  useEffect(() => {
    fetchNavieras();
  }, []);

  const fetchNavieras = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/navieras');
      setNavieras(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('Error al cargar navieras', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const openModal = (mode, naviera = null) => {
    setModalMode(mode);
    if (mode === 'edit' && naviera) {
      setEditingNaviera(naviera);
      setNombreNaviera(naviera.nombre);
      setDiasLibres(naviera.dias_libres || 21);
    } else {
      setEditingNaviera(null);
      setNombreNaviera('');
      setDiasLibres(21);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!nombreNaviera.trim()) {
      showToast('El nombre de la naviera es requerido', 'error');
      return;
    }

    if (diasLibres < 1 || diasLibres > 90) {
      showToast('Los días libres deben estar entre 1 y 90', 'error');
      return;
    }

    try {
      const endpoint = modalMode === 'create' ? '/navieras' : `/navieras/${editingNaviera.id}`;
      const method = modalMode === 'create' ? 'post' : 'put';

      await apiService[method](endpoint, {
        nombre: nombreNaviera.trim(),
        dias_libres: parseInt(diasLibres)
      });

      showToast(modalMode === 'create' ? 'Naviera creada exitosamente' : 'Naviera actualizada exitosamente', 'success');
      fetchNavieras();
      setShowModal(false);
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast(error.message || 'Error al guardar naviera', 'error');
      }
    }
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar la naviera "${nombre}"?\n\nEsto también eliminará todas sus tarifas asociadas.`)) return;

    try {
      await apiService.delete(`/navieras/${id}`);
      showToast('Naviera eliminada exitosamente', 'success');
      fetchNavieras();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast(error.message || 'Error al eliminar naviera', 'error');
      }
    }
  };

  const getDiasLibresColor = (dias) => {
    if (dias <= 10) return 'bg-red-100 text-red-800 border-red-300';
    if (dias <= 14) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (dias <= 21) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-ink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-ink-900 mx-auto mb-4"></div>
          <p className="text-ink-600">Cargando navieras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      <div className="fixed top-4 right-4 z-50 space-y-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
        ))}
      </div>

      <div className="flex items-center justify-between mb-8 animate-fade-in">
        <div>
          <h1 className="font-display text-3xl text-ink-900 mb-1">Navieras</h1>
          <p className="text-ink-400">Gestiona las navieras y sus días libres de demora</p>
        </div>
        <button onClick={() => openModal('create')} className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition shadow-soft">
          <Plus size={20} />
          <span>Nueva Naviera</span>
        </button>
      </div>

      {/* Leyenda de colores */}
      <div className="bg-white rounded-lg shadow-soft border border-ink-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-ink-900 mb-3">Leyenda de días libres:</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border text-xs font-semibold bg-red-100 text-red-800 border-red-300">1-10 días</span>
            <span className="text-xs text-ink-600">Políticas estrictas</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border text-xs font-semibold bg-orange-100 text-orange-800 border-orange-300">11-14 días</span>
            <span className="text-xs text-ink-600">Políticas moderadas</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border text-xs font-semibold bg-green-100 text-green-800 border-green-300">15-21 días</span>
            <span className="text-xs text-ink-600">Políticas estándar</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border text-xs font-semibold bg-blue-100 text-blue-800 border-blue-300">22+ días</span>
            <span className="text-xs text-ink-600">Políticas flexibles</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {navieras.map((naviera) => (
          <div key={naviera.id} className="bg-white rounded-xl shadow-soft border border-ink-200 p-5 hover:shadow-lifted transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 bg-ink-100 rounded-lg flex items-center justify-center">
                  <Ship className="text-ink-700" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-ink-900 text-lg">{naviera.nombre}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar size={14} className="text-ink-400" />
                    <span className={`px-2 py-0.5 rounded border text-xs font-semibold ${getDiasLibresColor(naviera.dias_libres)}`}>
                      {naviera.dias_libres || 21} días libres
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openModal('edit', naviera)} className="p-2 text-ink-700 hover:bg-ink-50 rounded-lg transition-colors" title="Editar">
                  <Edit2 size={18} />
                </button>
                <button onClick={() => handleDelete(naviera.id, naviera.nombre)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {navieras.length === 0 && (
        <div className="text-center py-12">
          <Ship size={64} className="mx-auto text-ink-300 mb-4" />
          <p className="text-ink-700 text-lg font-medium">No hay navieras registradas</p>
          <p className="text-ink-500 text-sm mt-1">Crea tu primera naviera para comenzar</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-lg shadow-lifted w-full max-w-md">
            <div className="px-6 py-4 border-b border-ink-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink-900">{modalMode === 'create' ? 'Nueva Naviera' : 'Editar Naviera'}</h2>
              <button onClick={() => setShowModal(false)} className="text-ink-400 hover:text-ink-600">
                <X size={24} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">Nombre de la Naviera *</label>
                <input
                  type="text"
                  value={nombreNaviera}
                  onChange={(e) => setNombreNaviera(e.target.value)}
                  className="w-full px-4 py-2.5 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 focus:outline-none"
                  placeholder="Ej: MAERSK, MSC, HAPAG LLOYD"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Días Libres (sin demoras) *
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={diasLibres}
                    onChange={(e) => setDiasLibres(parseInt(e.target.value) || 21)}
                    className="w-24 px-4 py-2.5 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 focus:outline-none text-center font-semibold"
                  />
                  <span className="text-ink-600">días</span>
                  <span className={`px-3 py-1.5 rounded-lg border text-sm font-semibold ${getDiasLibresColor(diasLibres)}`}>
                    {diasLibres <= 10 ? 'Estricto' : diasLibres <= 14 ? 'Moderado' : diasLibres <= 21 ? 'Estándar' : 'Flexible'}
                  </span>
                </div>
                <p className="text-xs text-ink-500 mt-2">
                  Número de días después del ATA antes de que inicien las demoras (típicamente 14-21 días)
                </p>
              </div>

              <div className="bg-ink-50 border border-ink-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-ink-700 flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="text-sm text-ink-900 font-medium">Días libres por naviera</p>
                    <p className="text-xs text-ink-600 mt-1">
                      Este valor determina cuándo el sistema comenzará a alertar sobre demoras próximas
                      y cuándo se empezarán a calcular los costos de demora.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition">
                Cancelar
              </button>
              <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition">
                <Check size={20} />
                <span>Guardar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Navieras;
